/**
 * Zone de vacances scolaires de chaque département métropolitain.
 *
 * **Le découpage est académique, pas départemental.** Le ministère publie trois zones
 * définies par académie ; ce qui est écrit ici en fait la projection sur les départements,
 * parce qu'un département appartient à une seule académie et qu'une ville se rattache à un
 * département, jamais directement à une académie. Le chemin est donc
 * `ville → département → académie → zone`, dont seule la dernière flèche est implicite.
 *
 * Les académies de rattachement figurent en commentaire de chaque bloc : elles sont la
 * source, et c'est par elles qu'il faudra relire cette table le jour où une académie change
 * de zone — ce qui arrive, la dernière refonte datant de 2015.
 *
 * **L'outre-mer n'y est pas, et ce n'est pas un oubli.** La Réunion, les Antilles, la
 * Guyane et Mayotte suivent des calendriers propres, calés sur leur hémisphère et leur
 * climat : les ranger dans une zone A, B ou C afficherait des vacances qui n'existent pas.
 * Un département absent de cette table rend `null`, et le produit se tait plutôt que de
 * mentir.
 */
export const ZONE_PAR_DEPARTEMENT = {
    // Zone A — Besançon
    '25': 'A', '39': 'A', '70': 'A', '90': 'A',
    // Zone A — Bordeaux
    '24': 'A', '33': 'A', '40': 'A', '47': 'A', '64': 'A',
    // Zone A — Clermont-Ferrand
    '03': 'A', '15': 'A', '43': 'A', '63': 'A',
    // Zone A — Dijon
    '21': 'A', '58': 'A', '71': 'A', '89': 'A',
    // Zone A — Grenoble
    '07': 'A', '26': 'A', '38': 'A', '73': 'A', '74': 'A',
    // Zone A — Limoges
    '19': 'A', '23': 'A', '87': 'A',
    // Zone A — Lyon
    '01': 'A', '42': 'A', '69': 'A',
    // Zone A — Poitiers
    '16': 'A', '17': 'A', '79': 'A', '86': 'A',
    // Zone B — Aix-Marseille
    '04': 'B', '05': 'B', '13': 'B', '84': 'B',
    // Zone B — Amiens
    '02': 'B', '60': 'B', '80': 'B',
    // Zone B — Corse
    '2A': 'B', '2B': 'B',
    // Zone B — Lille
    '59': 'B', '62': 'B',
    // Zone B — Nancy-Metz
    '54': 'B', '55': 'B', '57': 'B', '88': 'B',
    // Zone B — Nantes
    '44': 'B', '49': 'B', '53': 'B', '72': 'B', '85': 'B',
    // Zone B — Nice
    '06': 'B', '83': 'B',
    // Zone B — Normandie
    '14': 'B', '27': 'B', '50': 'B', '61': 'B', '76': 'B',
    // Zone B — Orléans-Tours
    '18': 'B', '28': 'B', '36': 'B', '37': 'B', '41': 'B', '45': 'B',
    // Zone B — Reims
    '08': 'B', '10': 'B', '51': 'B', '52': 'B',
    // Zone B — Rennes
    '22': 'B', '29': 'B', '35': 'B', '56': 'B',
    // Zone B — Strasbourg
    '67': 'B', '68': 'B',
    // Zone C — Créteil
    '77': 'C', '93': 'C', '94': 'C',
    // Zone C — Montpellier
    '11': 'C', '30': 'C', '34': 'C', '48': 'C', '66': 'C',
    // Zone C — Paris
    '75': 'C',
    // Zone C — Toulouse
    '09': 'C', '12': 'C', '31': 'C', '32': 'C', '46': 'C', '65': 'C', '81': 'C', '82': 'C',
    // Zone C — Versailles
    '78': 'C', '91': 'C', '92': 'C', '95': 'C',
};
/** Zone de vacances scolaires d'un département, ou `null` hors du champ de la table. */
export function schoolZoneForDepartment(departmentCode) {
    return ZONE_PAR_DEPARTEMENT[departmentCode] ?? null;
}
/**
 * Le département que désigne un code postal français.
 *
 * Les deux premiers chiffres, **sauf trois familles** que la règle générale range mal :
 *
 * - **La Corse.** `20xxx` ne correspond à aucun département : la Corse-du-Sud (2A) et la
 *   Haute-Corse (2B) partagent le préfixe. La coupure passe par le code postal complet —
 *   200xx et 201xx pour 2A, le reste pour 2B — approximation admise par la Poste elle-même
 *   et suffisante ici, les deux départements étant de toute façon dans la même zone.
 * - **L'outre-mer.** `97xxx` et `98xxx` demandent trois chiffres. Ils rendront `null` en
 *   zone scolaire, ce qui est correct : ces territoires ont leur propre calendrier.
 * - **Les cas où la règle ment.** Certaines communes ont un code postal d'un département
 *   voisin (Gex, Divonne…). On l'accepte : la zone qui en découle est celle du bureau
 *   distributeur, pas toujours celle de l'école, et l'écart reste rare devant le service
 *   rendu — une saisie de cinq chiffres au lieu d'une recherche de commune.
 */
export function departmentFromPostalCode(postalCode) {
    const propre = postalCode.replace(/\s/g, '');
    if (!/^\d{5}$/.test(propre))
        return null;
    const deux = propre.slice(0, 2);
    if (deux === '20')
        return Number(propre) < 20200 ? '2A' : '2B';
    if (deux === '97' || deux === '98')
        return propre.slice(0, 3);
    return deux;
}
/**
 * Zone de vacances scolaires d'un code postal, ou `null` si elle ne se déduit pas.
 *
 * C'est le chemin le plus court entre ce qu'un parent sait par cœur — les cinq chiffres de
 * l'école — et ce dont le calendrier a besoin. Il est aussi **plus juste que la résidence**
 * de l'enfant : un enfant peut habiter un département et être scolarisé dans un autre, ce
 * qui arrive dès qu'on vit près d'une limite d'académie, et ce sont les vacances de son
 * école qui font son calendrier.
 */
export function schoolZoneForPostalCode(postalCode) {
    const departement = departmentFromPostalCode(postalCode);
    return departement ? schoolZoneForDepartment(departement) : null;
}
//# sourceMappingURL=zones-france.js.map